import {
  pgTable,
  text,
  serial,
  integer,
  varchar,
  timestamp,
  jsonb,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// ── feature_usage_events ────────────────────────────────────────────────────
// Append-only event stream. Powers the Feature Analytics and Client Health
// dashboards. Indexes target the common query shapes: by user + time, by
// feature + time, by client + time, by role + time.
//
// Volume note: this table grows fast. Plan for monthly partitioning or a
// rollup job (e.g. feature_usage_daily) when row counts cross ~10M. For now,
// the indexes handle "last 90 days" filters cheaply enough.
export const featureUsageEvents = pgTable(
  "feature_usage_events",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id", { length: 255 }).notNull(),
    userRole: varchar("user_role", { length: 50 }).notNull(),
    clientId: integer("client_id"),         // nullable for cross-client events
    featureName: varchar("feature_name", { length: 100 }).notNull(),
    featureCategory: varchar("feature_category", { length: 50 }),
    pagePath: text("page_path"),
    action: varchar("action", { length: 50 }).notNull().default("page_view"),
    sessionId: varchar("session_id", { length: 100 }),
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (t) => ({
    userCreatedIdx: index("idx_feature_usage_user_created").on(t.userId, t.createdAt),
    featureCreatedIdx: index("idx_feature_usage_feature_created").on(t.featureName, t.createdAt),
    clientCreatedIdx: index("idx_feature_usage_client_created").on(t.clientId, t.createdAt),
    roleCreatedIdx: index("idx_feature_usage_role_created").on(t.userRole, t.createdAt),
  })
);

export const insertFeatureUsageEventSchema = createInsertSchema(featureUsageEvents)
  .omit({ id: true, createdAt: true })
  .extend({
    featureName: z.string().min(1).max(100),
    action: z.string().min(1).max(50).default("page_view"),
  });

export type FeatureUsageEvent = typeof featureUsageEvents.$inferSelect;
export type InsertFeatureUsageEvent = z.infer<typeof insertFeatureUsageEventSchema>;
